/**
 * BdcAppointments — industrial calendar for the BDC team.
 *
 * ── Views ─────────────────────────────────────────────────────────────
 *   Week (default) · Month · Day · Agenda.  Powered by `react-big-calendar`
 *   with the drag-and-drop HOC. 15-minute snapping. Local timezone via the
 *   date-fns localizer.
 *
 * ── Drag & Drop ──────────────────────────────────────────────────────
 *   Dragging or resizing a scheduled appointment fires
 *   `PATCH /api/bdc/appointments/:id/reschedule` on the backend which:
 *     • Cascades reminder emails (queued rows for old time deleted, new
 *       ones enqueued for `new - 24h`)
 *     • Writes an audit-log row `bdc.appointment.rescheduled`
 *   Only `scheduled` appointments are draggable — terminal states are
 *   read-only.
 *
 * ── New Appointment Modal ────────────────────────────────────────────
 *   Two paths:
 *     1) Lookup — enter email/phone → hit `/api/bdc/leads/lookup` →
 *        surface up to 5 matching non-terminal leads → attach.
 *     2) Fresh — pick dealer + vehicle + fill guest fields → creates a
 *        brand-new Lead + Appointment atomically.
 *   Prevents duplicate leads by always offering match first.
 *
 * ── Status Color-coding ──────────────────────────────────────────────
 *   scheduled  → amber
 *   showed_up  → emerald
 *   no_show    → rose
 *   cancelled  → slate
 *
 * ── Data flow ────────────────────────────────────────────────────────
 *   The calendar's `range` prop change triggers a fetch bounded to the
 *   visible window (`date_from` + `date_to` params on GET /appointments).
 *   `mine_only` toggle scopes to the current BDC agent.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Calendar, dateFnsLocalizer, Views } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import {
  addDays, addHours, endOfMonth, endOfWeek, format, getDay, parse, parseISO,
  startOfDay, startOfMonth, startOfWeek,
} from "date-fns";
import { enUS } from "date-fns/locale";
import {
  CalendarPlus, CheckCircle2, Loader2, MapPin, Phone as PhoneIcon,
  Plus, RefreshCcw, Search, User, X, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatUSD } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { PageContainer, PageHeader, StatusPill, APPT_STATUS } from "./bdcUi";

// ---------------------------------------------------------------------------
// Setup
// ---------------------------------------------------------------------------

const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

const DnDCalendar = withDragAndDrop(Calendar);

// Phase 11 — added `confirmed` (BDC has confirmed the customer is coming).
// Same green family as showed_up but slightly lighter so the two can be told
// apart on the calendar.
const STATUS_STYLE = {
  scheduled: { background: "#1B3A6B", border: "#0f2447", text: "#FFFFFF" },
  confirmed: { background: "#10B981", border: "#047857", text: "#FFFFFF" },
  showed_up: { background: "#059669", border: "#065F46", text: "#FFFFFF" },
  no_show: { background: "#F59E0B", border: "#B45309", text: "#FFFFFF" },
  cancelled: { background: "#94A3B8", border: "#475569", text: "#FFFFFF" },
  rescheduled: { background: "#3B82F6", border: "#1E40AF", text: "#FFFFFF" },
};

const DEFAULT_DURATION_MIN = 30;

// ---------------------------------------------------------------------------
// New Appointment Modal
// ---------------------------------------------------------------------------

const initialFreshForm = () => ({
  scheduled_at: "",
  duration_minutes: DEFAULT_DURATION_MIN,
  dealer_id: "",
  vehicle_id: "",
  guest_name: "",
  guest_email: "",
  guest_phone: "",
  notes: "",
});

const roundTo15 = (date) => {
  const d = new Date(date);
  const ms = 15 * 60 * 1000;
  return new Date(Math.round(d.getTime() / ms) * ms);
};

const toDatetimeLocal = (date) => {
  // The <input type="datetime-local"> value needs YYYY-MM-DDTHH:mm in *local*
  // time (no Z suffix). Do the offset math by hand so we don't get UTC-shift.
  const d = new Date(date);
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 16);
};

const NewAppointmentModal = ({
  open, onClose, onCreated, dealers, initialSlot,
}) => {
  // Step machine:
  //   'lookup' — search box + candidate list (default)
  //   'fresh'  — dealer/vehicle/guest form (user clicked "No — create new")
  const [step, setStep] = useState("lookup");
  const [lookupQuery, setLookupQuery] = useState({ email: "", phone: "" });
  const [candidates, setCandidates] = useState([]);
  const [searchedOnce, setSearchedOnce] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  const [freshForm, setFreshForm] = useState(initialFreshForm());
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const activeDealers = useMemo(
    () => (dealers || []).filter((d) => !d.is_coming_soon && d.is_active !== false),
    [dealers],
  );

  useEffect(() => {
    if (!open) return;
    setStep("lookup");
    setLookupQuery({ email: "", phone: "" });
    setCandidates([]);
    setSearchedOnce(false);
    setSelectedLead(null);
    const seed = initialSlot ? toDatetimeLocal(roundTo15(initialSlot)) : "";
    setFreshForm({ ...initialFreshForm(), scheduled_at: seed });
  }, [open, initialSlot]);

  useEffect(() => {
    // Load dealer's inventory whenever the dealer changes in the "fresh" form.
    if (step !== "fresh" || !freshForm.dealer_id) {
      setVehicles([]);
      return;
    }
    setVehiclesLoading(true);
    api
      .get(`/dealers/${freshForm.dealer_id}`)
      .then((r) => setVehicles((r.data.vehicles || []).filter((v) => v.status === "available")))
      .catch(() => setVehicles([]))
      .finally(() => setVehiclesLoading(false));
  }, [step, freshForm.dealer_id]);

  const doLookup = async () => {
    const { email, phone } = lookupQuery;
    if (!email && !phone) {
      toast.info("Enter an email or phone to search.");
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.get("/bdc/leads/lookup", {
        params: { email: email || undefined, phone: phone || undefined },
      });
      setCandidates(data || []);
      setSearchedOnce(true);
      if (!data?.length) {
        toast.info("No matching lead found — you can create a new one.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSearching(false);
    }
  };

  const attachToLead = async () => {
    if (!selectedLead) return;
    const localScheduled = freshForm.scheduled_at;
    if (!localScheduled) {
      toast.error("Pick a date & time.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/bdc/appointments", {
        lead_id: selectedLead.id,
        scheduled_at: new Date(localScheduled).toISOString(),
        duration_minutes: Number(freshForm.duration_minutes) || DEFAULT_DURATION_MIN,
        notes: freshForm.notes || undefined,
      });
      toast.success("Appointment booked");
      onCreated?.(data);
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const createFresh = async () => {
    const {
      scheduled_at, duration_minutes, dealer_id, vehicle_id,
      guest_name, guest_email, guest_phone, notes,
    } = freshForm;
    if (!scheduled_at) return toast.error("Pick a date & time.");
    if (!dealer_id) return toast.error("Pick a dealer.");
    if (!vehicle_id) return toast.error("Pick a vehicle.");
    if (!guest_name) return toast.error("Enter customer name.");
    if (!guest_email) return toast.error("Enter customer email.");
    setSaving(true);
    try {
      const { data } = await api.post("/bdc/appointments", {
        scheduled_at: new Date(scheduled_at).toISOString(),
        duration_minutes: Number(duration_minutes) || DEFAULT_DURATION_MIN,
        dealer_id, vehicle_id,
        guest_name, guest_email, guest_phone,
        notes: notes || undefined,
      });
      toast.success("Appointment + lead created");
      onCreated?.(data);
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="new-appt-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarPlus size={18} className="text-brand" />
            New appointment
          </DialogTitle>
          <DialogDescription>
            Search by email or phone first — attaches to an existing lead when we find one, avoiding duplicates.
          </DialogDescription>
        </DialogHeader>

        {step === "lookup" ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Email</Label>
                <Input
                  type="email"
                  value={lookupQuery.email}
                  onChange={(e) => setLookupQuery((s) => ({ ...s, email: e.target.value }))}
                  placeholder="customer@example.com"
                  className="mt-1"
                  data-testid="new-appt-lookup-email"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Phone</Label>
                <Input
                  value={lookupQuery.phone}
                  onChange={(e) => setLookupQuery((s) => ({ ...s, phone: e.target.value }))}
                  placeholder="(555) 555-0100"
                  className="mt-1"
                  data-testid="new-appt-lookup-phone"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={doLookup}
                disabled={searching}
                className="bg-brand hover:bg-brand-hover text-white"
                data-testid="new-appt-lookup-btn"
              >
                {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                Search existing leads
              </Button>
              <Button
                variant="outline"
                onClick={() => setStep("fresh")}
                data-testid="new-appt-goto-fresh"
              >
                <Plus size={14} /> No — create new
              </Button>
            </div>

            {searchedOnce && candidates.length > 0 && (
              <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-[280px] overflow-y-auto">
                {candidates.map((c) => (
                  <label
                    key={c.id}
                    className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50 ${
                      selectedLead?.id === c.id ? "bg-brand/5" : ""
                    }`}
                    data-testid={`new-appt-candidate-${c.id}`}
                  >
                    <input
                      type="radio"
                      name="lead-candidate"
                      checked={selectedLead?.id === c.id}
                      onChange={() => setSelectedLead(c)}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">{c.customer_name || "Guest"}</span>
                        <StatusPill tone="blue">{c.status}</StatusPill>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {c.customer_email}{c.customer_phone ? ` · ${c.customer_phone}` : ""}
                      </p>
                      {c.vehicle && (
                        <p className="text-xs text-slate-600 mt-0.5">
                          {c.vehicle.year} {c.vehicle.make} {c.vehicle.model} @ {c.dealer?.name || "—"}
                        </p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}

            {selectedLead && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 border-t border-slate-100 pt-4">
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                    Scheduled for
                  </Label>
                  <Input
                    type="datetime-local"
                    step={900}
                    value={freshForm.scheduled_at}
                    onChange={(e) => setFreshForm((s) => ({ ...s, scheduled_at: e.target.value }))}
                    className="mt-1"
                    data-testid="new-appt-attach-datetime"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                    Duration (min)
                  </Label>
                  <Input
                    type="number" min={15} step={15}
                    value={freshForm.duration_minutes}
                    onChange={(e) => setFreshForm((s) => ({ ...s, duration_minutes: Number(e.target.value) }))}
                    className="mt-1"
                    data-testid="new-appt-attach-duration"
                  />
                </div>
                <div className="sm:col-span-3">
                  <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                    Internal notes (optional)
                  </Label>
                  <Textarea
                    rows={2}
                    value={freshForm.notes}
                    onChange={(e) => setFreshForm((s) => ({ ...s, notes: e.target.value }))}
                    className="mt-1"
                    data-testid="new-appt-attach-notes"
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                  Scheduled for
                </Label>
                <Input
                  type="datetime-local"
                  step={900}
                  value={freshForm.scheduled_at}
                  onChange={(e) => setFreshForm((s) => ({ ...s, scheduled_at: e.target.value }))}
                  className="mt-1"
                  data-testid="new-appt-fresh-datetime"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                  Duration (min)
                </Label>
                <Input
                  type="number" min={15} step={15}
                  value={freshForm.duration_minutes}
                  onChange={(e) => setFreshForm((s) => ({ ...s, duration_minutes: Number(e.target.value) }))}
                  className="mt-1"
                  data-testid="new-appt-fresh-duration"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Dealer</Label>
                <Select
                  value={freshForm.dealer_id}
                  onValueChange={(v) => setFreshForm((s) => ({ ...s, dealer_id: v, vehicle_id: "" }))}
                >
                  <SelectTrigger className="mt-1" data-testid="new-appt-fresh-dealer">
                    <SelectValue placeholder="Choose a dealer" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeDealers.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Vehicle</Label>
                <Select
                  value={freshForm.vehicle_id}
                  onValueChange={(v) => setFreshForm((s) => ({ ...s, vehicle_id: v }))}
                  disabled={!freshForm.dealer_id || vehiclesLoading}
                >
                  <SelectTrigger className="mt-1" data-testid="new-appt-fresh-vehicle">
                    <SelectValue placeholder={vehiclesLoading ? "Loading…" : "Choose a vehicle"} />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {vehicles.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.year} {v.make} {v.model} · {formatUSD(v.price)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Name</Label>
                <Input
                  value={freshForm.guest_name}
                  onChange={(e) => setFreshForm((s) => ({ ...s, guest_name: e.target.value }))}
                  className="mt-1"
                  data-testid="new-appt-fresh-name"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Email</Label>
                <Input
                  type="email"
                  value={freshForm.guest_email}
                  onChange={(e) => setFreshForm((s) => ({ ...s, guest_email: e.target.value }))}
                  className="mt-1"
                  data-testid="new-appt-fresh-email"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Phone</Label>
                <Input
                  value={freshForm.guest_phone}
                  onChange={(e) => setFreshForm((s) => ({ ...s, guest_phone: e.target.value }))}
                  className="mt-1"
                  data-testid="new-appt-fresh-phone"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Internal notes</Label>
              <Textarea
                rows={2}
                value={freshForm.notes}
                onChange={(e) => setFreshForm((s) => ({ ...s, notes: e.target.value }))}
                className="mt-1"
                data-testid="new-appt-fresh-notes"
              />
            </div>
          </div>
        )}

        <DialogFooter className="pt-2">
          {step === "fresh" && (
            <Button variant="ghost" onClick={() => setStep("lookup")} disabled={saving}>
              ← Back to lookup
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          {step === "lookup" ? (
            <Button
              className="bg-brand text-white hover:bg-brand-hover"
              onClick={attachToLead}
              disabled={!selectedLead || saving}
              data-testid="new-appt-attach-submit"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Book on this lead
            </Button>
          ) : (
            <Button
              className="bg-brand text-white hover:bg-brand-hover"
              onClick={createFresh}
              disabled={saving}
              data-testid="new-appt-fresh-submit"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <CalendarPlus size={14} />}
              Create lead + appointment
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Outcome (Show / No-Show / Cancel) + Detail Dialog
// ---------------------------------------------------------------------------

const EventDetailDialog = ({ appt, onClose, onOutcome, onMarkConfirmed, saving }) => {
  const [notes, setNotes] = useState("");
  const [outcome, setOutcome] = useState(null);
  useEffect(() => { setNotes(""); setOutcome(null); }, [appt?.id]);

  if (!appt) return null;
  const at = parseISO(appt.scheduled_at);

  return (
    <Dialog open={!!appt} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="appt-detail-dialog">
        <DialogHeader>
          <DialogTitle>Appointment · {format(at, "EEE MMM d, h:mm a")}</DialogTitle>
          <DialogDescription>
            {appt.customer?.full_name || appt.guest_name || "Guest"} — {appt.vehicle
              ? `${appt.vehicle.year} ${appt.vehicle.make} ${appt.vehicle.model}`
              : "no vehicle"}
          </DialogDescription>
        </DialogHeader>

        <div className="text-sm text-slate-700 space-y-1">
          <p className="inline-flex items-center gap-1"><MapPin size={13} className="text-slate-400" /> {appt.dealer?.name || "—"}</p>
          {appt.guest_phone && <p className="inline-flex items-center gap-1"><PhoneIcon size={13} className="text-slate-400" /> {appt.guest_phone}</p>}
          <p className="inline-flex items-center gap-1">
            <User size={13} className="text-slate-400" /> Status:
            <StatusPill tone={APPT_STATUS[appt.status]?.tone || "slate"} className="ml-1">
              {APPT_STATUS[appt.status]?.label || appt.status}
            </StatusPill>
          </p>
        </div>

        {appt.status === "scheduled" && (
          <>
            <div className="grid grid-cols-3 gap-2 mt-1">
              <Button
                variant={outcome === "showed_up" ? "default" : "outline"}
                className={outcome === "showed_up" ? "bg-emerald-600 hover:bg-emerald-500 text-white" : "border-emerald-200 text-emerald-700"}
                onClick={() => setOutcome("showed_up")}
                data-testid="appt-outcome-showed-up"
              >
                <CheckCircle2 size={14} /> Showed up
              </Button>
              <Button
                variant={outcome === "no_show" ? "default" : "outline"}
                className={outcome === "no_show" ? "bg-rose-600 hover:bg-rose-500 text-white" : "border-rose-200 text-rose-700"}
                onClick={() => setOutcome("no_show")}
                data-testid="appt-outcome-no-show"
              >
                <X size={14} /> No-show
              </Button>
              <Button
                variant={outcome === "cancelled" ? "default" : "outline"}
                className={outcome === "cancelled" ? "bg-slate-600 hover:bg-slate-500 text-white" : ""}
                onClick={() => setOutcome("cancelled")}
                data-testid="appt-outcome-cancel"
              >
                <XCircle size={14} /> Cancel
              </Button>
            </div>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes…"
              data-testid="appt-outcome-notes"
            />
          </>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Close</Button>
          {/* Phase 11 — Mark as Confirmed pre-outcome, only when scheduled */}
          {appt.status === "scheduled" && onMarkConfirmed && (
            <Button
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={() => onMarkConfirmed(appt.id)}
              disabled={saving}
              data-testid="appt-mark-confirmed-btn"
            >
              <CheckCircle2 size={14} /> Mark as Confirmed
            </Button>
          )}
          {appt.status === "scheduled" && (
            <Button
              className="bg-brand text-white hover:bg-brand-hover"
              disabled={!outcome || saving}
              onClick={() => onOutcome(outcome, notes.trim())}
              data-testid="appt-outcome-confirm"
            >
              {saving ? "Saving…" : "Confirm outcome"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CalendarEvent = ({ event }) => (
  <div className="text-[11px] leading-tight" data-testid={`bdc-appt-event-${event.id}`}>
    <div className="font-semibold truncate">
      {format(event.start, "h:mm a")} · {event.resource.customer?.full_name || event.resource.guest_name || "Guest"}
    </div>
    {event.resource.vehicle && (
      <div className="opacity-90 truncate">
        {event.resource.vehicle.year} {event.resource.vehicle.make} {event.resource.vehicle.model}
      </div>
    )}
  </div>
);

const CALENDAR_COMPONENTS = { event: CalendarEvent };

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const BdcAppointments = () => {
  const [view, setView] = useState(Views.WEEK);
  const [date, setDate] = useState(new Date());
  const [mineOnly, setMineOnly] = useState(false);
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSlot, setModalSlot] = useState(null);
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [saving, setSaving] = useState(false);

  // Fetch a slightly wider window than the current view so navigation feels
  // instant (still bounded — no 1000-doc round-trips).
  const range = useMemo(() => {
    if (view === Views.MONTH) {
      const from = addDays(startOfMonth(date), -7);
      const to = addDays(endOfMonth(date), 7);
      return { from, to };
    }
    if (view === Views.DAY) {
      return { from: startOfDay(date), to: addDays(startOfDay(date), 1) };
    }
    // Week + Agenda
    const from = startOfWeek(date, { weekStartsOn: 1 });
    const to = addDays(endOfWeek(date, { weekStartsOn: 1 }), 1);
    return { from, to };
  }, [view, date]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bdc/appointments", {
        params: {
          date_from: range.from.toISOString(),
          date_to: range.to.toISOString(),
          mine_only: mineOnly,
        },
      });
      setAppts(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, mineOnly]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/dealers").then((r) => setDealers(r.data)).catch(() => setDealers([]));
  }, []);

  const events = useMemo(() => appts.map((a) => {
    const start = parseISO(a.scheduled_at);
    const end = addHours(start, (a.duration_minutes || DEFAULT_DURATION_MIN) / 60);
    const title = `${a.customer?.full_name || a.guest_name || "Guest"}${a.vehicle ? ` · ${a.vehicle.year} ${a.vehicle.make} ${a.vehicle.model}` : ""}`;
    return {
      id: a.id,
      title,
      start,
      end,
      resource: a,
      // Draggable only in `scheduled` status — react-big-calendar respects
      // both flags on a per-event basis (`isDraggable`/`isResizable`).
      isDraggable: a.status === "scheduled",
      isResizable: a.status === "scheduled",
    };
  }), [appts]);

  const showedUpCount = appts.filter((a) => a.status === "showed_up").length;

  const eventStyleGetter = (event) => {
    const s = STATUS_STYLE[event.resource.status] || STATUS_STYLE.scheduled;
    return {
      style: {
        backgroundColor: s.background,
        borderLeft: `4px solid ${s.border}`,
        color: s.text,
        borderRadius: 6,
        padding: "2px 6px",
        fontSize: 12,
        fontWeight: 600,
      },
    };
  };

  const applyReschedule = async (event, { start, end }) => {
    const appt = event.resource;
    if (appt.status !== "scheduled") {
      toast.info("Only scheduled appointments can be moved.");
      return;
    }
    const newIso = start.toISOString();
    const durationMin = Math.max(15, Math.round((end.getTime() - start.getTime()) / 60000));

    // Optimistic update — snap event into place before the round-trip completes.
    setAppts((prev) => prev.map((a) => (
      a.id === appt.id ? { ...a, scheduled_at: newIso, duration_minutes: durationMin } : a
    )));

    try {
      await api.patch(`/bdc/appointments/${appt.id}/reschedule`, {
        scheduled_at: newIso,
        duration_minutes: durationMin,
        reason: "Rescheduled via calendar drag-and-drop",
      });
      toast.success("Rescheduled — reminder emails updated");
    } catch (e) {
      toast.error(formatApiError(e));
      // Roll back on failure
      load();
    }
  };

  const submitOutcome = async (outcomeKind, notes) => {
    if (!selectedAppt || !outcomeKind) return;
    setSaving(true);
    try {
      await api.patch(`/bdc/appointments/${selectedAppt.id}/outcome`, {
        outcome: outcomeKind,
        notes: notes || undefined,
      });
      toast.success("Outcome saved");
      setSelectedAppt(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // Phase 11 — flip a scheduled appt to confirmed (BDC has personally
  // confirmed the customer is coming). Commission is still only paid on
  // showed_up; this is a pre-outcome status.
  const markConfirmed = async (apptId) => {
    setSaving(true);
    try {
      await api.post(`/bdc/appointments/${apptId}/confirm`);
      toast.success("Marked as Confirmed");
      setSelectedAppt(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const openNewFromSlot = ({ start }) => {
    setModalSlot(start);
    setModalOpen(true);
  };

  return (
    <PageContainer>
      <PageHeader
        title="Appointments"
        subtitle="Drag to reschedule · click a slot to create · $300 billable per showed-up outcome."
        testId="bdc-appts-header"
        right={
          <>
            <Tabs value={mineOnly ? "mine" : "all"} onValueChange={(v) => setMineOnly(v === "mine")}>
              <TabsList>
                <TabsTrigger value="all" data-testid="bdc-appts-tab-all">All BDC</TabsTrigger>
                <TabsTrigger value="mine" data-testid="bdc-appts-tab-mine">Mine</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button
              onClick={() => { setModalSlot(null); setModalOpen(true); }}
              className="bg-brand text-white hover:bg-brand-hover"
              data-testid="bdc-appts-new-btn"
            >
              <CalendarPlus size={15} /> New appointment
            </Button>
            <Button variant="outline" onClick={load} data-testid="bdc-appts-refresh">
              <RefreshCcw size={15} /> Refresh
            </Button>
          </>
        }
      />

      <div className="rounded-2xl border border-slate-200 bg-white p-4 mb-4 text-sm flex flex-wrap items-center gap-x-4 gap-y-1">
        <span><span className="font-semibold text-slate-800">{appts.length}</span> appointment{appts.length === 1 ? "" : "s"} in view</span>
        <span className="text-slate-300">·</span>
        <span><span className="font-semibold text-emerald-700">{showedUpCount}</span> showed up</span>
        <span className="text-slate-300">·</span>
        <span className="font-semibold text-slate-900">{formatUSD(showedUpCount * 300)}</span> billable
        {loading && <Loader2 size={14} className="ml-2 animate-spin text-slate-400" />}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="bdc-appts-calendar">
        <div className="p-3 sm:p-4">
          <DnDCalendar
            localizer={localizer}
            events={events}
            date={date}
            view={view}
            onNavigate={setDate}
            onView={setView}
            views={{
              [Views.MONTH]: true,
              [Views.WEEK]: true,
              [Views.DAY]: true,
              [Views.AGENDA]: true,
            }}
            step={15}
            timeslots={4}
            style={{ height: "70vh", minHeight: 620 }}
            selectable
            onSelectSlot={openNewFromSlot}
            onSelectEvent={(evt) => setSelectedAppt(evt.resource)}
            onEventDrop={applyReschedule}
            onEventResize={applyReschedule}
            resizable
            popup
            eventPropGetter={eventStyleGetter}
            draggableAccessor={(e) => e.isDraggable}
            resizableAccessor={(e) => e.isResizable}
            components={CALENDAR_COMPONENTS}
          />
        </div>

        {/* Legend — matches the color coding of events on the calendar. */}
        <div
          className="flex flex-wrap items-center gap-3 border-t border-slate-100 px-4 sm:px-5 py-3 text-[11px] text-slate-500"
          data-testid="bdc-appts-legend"
        >
          <span className="font-semibold text-slate-700 uppercase tracking-widest">Legend:</span>
          {Object.entries(STATUS_STYLE).map(([key, s]) => (
            APPT_STATUS[key] ? (
              <span key={key} className="inline-flex items-center gap-1">
                <span
                  className="w-3 h-3 rounded"
                  style={{ backgroundColor: s.background, borderLeft: `3px solid ${s.border}` }}
                />
                {APPT_STATUS[key].label}
              </span>
            ) : null
          ))}
        </div>
      </div>

      <NewAppointmentModal
        open={modalOpen}
        initialSlot={modalSlot}
        dealers={dealers}
        onClose={() => setModalOpen(false)}
        onCreated={() => load()}
      />
      <EventDetailDialog
        appt={selectedAppt}
        onClose={() => setSelectedAppt(null)}
        onOutcome={submitOutcome}
        onMarkConfirmed={markConfirmed}
        saving={saving}
      />
    </PageContainer>
  );
};

export default BdcAppointments;
